import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import BreedCard from "@/components/BreedCard";
import { getBreeds } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { usePageMeta } from "@/lib/usePageMeta";

export default function BreedBrowser() {
  usePageMeta("Browse 105 Dog Breeds — Prices, Traits & Costs | Fetchright", "Compare 105 dog breeds by size, energy, budget and family fit — with typical Australian puppy prices and monthly ownership costs.");
  const [breeds, setBreeds] = useState([]);
  const [q, setQ] = useState("");
  const [size, setSize] = useState("any");
  const [energyMax, setEnergyMax] = useState(5);
  const [budget, setBudget] = useState(8000); // 8000 = no budget cap
  const [apartment, setApartment] = useState(false);
  const [kids, setKids] = useState(false);
  const [allergy, setAllergy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (size !== "any") params.size = size;
      if (energyMax < 5) params.energy_max = energyMax;
      if (apartment) params.apartment_ok = 4;
      if (kids) params.good_with_kids = 4;
      if (allergy) params.allergy_friendly = true;
      const data = await getBreeds(params);
      setBreeds(data);
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [q, size, energyMax, apartment, kids, allergy]);

  // Budget filter is client-side: a breed fits if its lowest typical price is within budget.
  const visible = budget < 8000
    ? breeds.filter((b) => b.purchase_price && b.purchase_price.min_aud <= budget)
    : breeds;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-10">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Breed browser</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
          Explore every breed
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Curated profiles with the details that actually matter: energy, shedding, apartment-friendliness, and how good they are with kids.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="rounded-3xl bg-card border border-border/60 p-6 h-fit warm-shadow lg:sticky lg:top-24">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="breed-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search breeds…"
              className="pl-9 rounded-full border-border/70"
            />
          </div>

          <div className="mt-6 space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Size</Label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger data-testid="filter-size" className="rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any size</SelectItem>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
                <SelectItem value="giant">Giant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max energy</Label>
              <span className="text-sm font-semibold text-accent">{energyMax}/5</span>
            </div>
            <Slider
              data-testid="filter-energy"
              value={[energyMax]}
              onValueChange={([v]) => setEnergyMax(v)}
              min={1}
              max={5}
              step={1}
            />
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max budget to buy</Label>
              <span data-testid="budget-value" className="text-sm font-semibold text-accent">
                {budget < 8000 ? `$${budget.toLocaleString()}` : "Any"}
              </span>
            </div>
            <Slider
              data-testid="filter-budget"
              value={[budget]}
              onValueChange={([v]) => setBudget(v)}
              min={1000}
              max={8000}
              step={500}
            />
          </div>

          {[
            { label: "Apartment friendly", value: apartment, setter: setApartment, id: "apartment" },
            { label: "Great with kids", value: kids, setter: setKids, id: "kids" },
            { label: "Allergy friendly", value: allergy, setter: setAllergy, id: "allergy" },
          ].map((f) => (
            <div key={f.id} className="mt-5 flex items-center justify-between">
              <Label htmlFor={`sw-${f.id}`} className="text-sm text-accent font-medium">{f.label}</Label>
              <Switch
                id={`sw-${f.id}`}
                data-testid={`filter-${f.id}`}
                checked={f.value}
                onCheckedChange={f.setter}
              />
            </div>
          ))}
        </aside>

        <div>
          {(() => {
            if (loading) return <div data-testid="breeds-loading" className="text-muted-foreground">Loading…</div>;
            if (visible.length === 0) return <div className="text-muted-foreground">No breeds match — try relaxing your filters.</div>;
            return (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {visible.map((b) => <BreedCard key={b.id} breed={b} />)}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
